interface RadarChartProps {
    axes: Array<{
        label: string
        value: number   // raw value
        min: number     // expected min
        max: number     // expected max
        unit?: string
    }>
    size?: number
}

export default function RadarChart({ axes, size = 200 }: RadarChartProps) {
    if (axes.length < 3) return null

    const cx = size / 2
    const cy = size / 2
    const radius = size / 2 - 30

    const n = axes.length
    const angleStep = (Math.PI * 2) / n

    // Normalize values to 0-1
    const normalized = axes.map(a => {
        const clamped = Math.max(a.min, Math.min(a.max, a.value))
        return (clamped - a.min) / (a.max - a.min || 1)
    })

    // Calculate polygon points
    const dataPoints = normalized.map((v, i) => {
        const angle = -Math.PI / 2 + i * angleStep
        const r = v * radius
        return {
            x: cx + r * Math.cos(angle),
            y: cy + r * Math.sin(angle)
        }
    })

    // Grid rings (3 concentric)
    const rings = [0.33, 0.66, 1.0]

    // Axis endpoints
    const axisEnds = axes.map((_, i) => {
        const angle = -Math.PI / 2 + i * angleStep
        return {
            x: cx + radius * Math.cos(angle),
            y: cy + radius * Math.sin(angle),
            lx: cx + (radius + 16) * Math.cos(angle),
            ly: cy + (radius + 16) * Math.sin(angle)
        }
    })

    const polygonPath = dataPoints.map((p, i) =>
        `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`
    ).join(' ') + ' Z'

    // Health color based on how centered the values are
    const avgNorm = normalized.reduce((s, v) => s + v, 0) / n
    const healthHue = avgNorm > 0.7 ? 0 : avgNorm > 0.4 ? 50 : 140  // red → yellow → green

    return (
        <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className="mx-auto"
        >
            {/* Grid rings */}
            {rings.map((r, i) => (
                <polygon
                    key={i}
                    points={axes.map((_, j) => {
                        const angle = -Math.PI / 2 + j * angleStep
                        const rr = r * radius
                        return `${(cx + rr * Math.cos(angle)).toFixed(1)},${(cy + rr * Math.sin(angle)).toFixed(1)}`
                    }).join(' ')}
                    fill="none"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="0.5"
                />
            ))}

            {/* Axis lines */}
            {axisEnds.map((end, i) => (
                <line
                    key={i}
                    x1={cx} y1={cy}
                    x2={end.x} y2={end.y}
                    stroke="rgba(255,255,255,0.15)"
                    strokeWidth="0.5"
                />
            ))}

            {/* Data polygon */}
            <path
                d={polygonPath}
                fill={`hsla(${healthHue}, 80%, 55%, 0.2)`}
                stroke={`hsla(${healthHue}, 80%, 55%, 0.8)`}
                strokeWidth="1.5"
                strokeLinejoin="round"
            />

            {/* Data points */}
            {dataPoints.map((p, i) => (
                <circle
                    key={i}
                    cx={p.x} cy={p.y}
                    r="3"
                    fill={`hsla(${healthHue}, 80%, 55%, 0.9)`}
                    stroke="white"
                    strokeWidth="1"
                />
            ))}

            {/* Labels */}
            {axisEnds.map((end, i) => (
                <text
                    key={i}
                    x={end.lx}
                    y={end.ly}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="rgba(255,255,255,0.6)"
                    fontSize="9"
                    fontFamily="Inter, sans-serif"
                >
                    {axes[i].label}
                </text>
            ))}

            {/* Value labels (inside, near data points) */}
            {dataPoints.map((p, i) => {
                const val = axes[i].value
                const display = typeof val === 'number' ? val.toFixed(1) : val
                return (
                    <text
                        key={`v${i}`}
                        x={p.x}
                        y={p.y - 8}
                        textAnchor="middle"
                        fill="rgba(255,255,255,0.8)"
                        fontSize="8"
                        fontFamily="Inter, sans-serif"
                        fontWeight="600"
                    >
                        {display}{axes[i].unit ? ` ${axes[i].unit}` : ''}
                    </text>
                )
            })}
        </svg>
    )
}
